import { motion } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHead, FieldLabel, FieldError } from "@/components/inquiry/inquiryFields";

const BUDGET_OPTIONS = [
    "Under $1,000",
    "$1,000 – $3,000",
    "$3,000 – $7,500",
    "$7,500 – $15,000",
    "$15,000 – $35,000",
    "$35,000+",
];

const TIMELINES = [
    "ASAP (rush)",
    "2 – 3 weeks",
    "1 – 2 months",
    "2 – 4 months",
    "Flexible",
];

/**
 * StepBudget — Custom Project variant of the investment step.
 * No package price anchor — budget-first mindset. Same field storage as
 * `StepInvestment` (budget_mode / estimated_budget / custom_budget / expected_timeline)
 * so submission logic can stay unified.
 */
export const StepBudget = ({ data, onField, errors }) => (
    <section data-testid="step-budget">
        <SectionHead
            eyebrow="Step 05 · Investment"
            title="What’s your budget for this Custom Project?"
            description="We treat every budget confidentially. Sharing a real number lets us tailor scope to what genuinely fits."
        />

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {/* Budget mode toggle + input */}
            <div className="flex flex-col gap-2">
                <FieldLabel htmlFor="budget-picker">Budget</FieldLabel>

                <div className="flex gap-1.5" role="tablist" aria-label="Budget mode">
                    <button
                        type="button"
                        onClick={() => onField("budget_mode", "range")}
                        data-testid="budget-mode-range"
                        className="flex-1 rounded-lg border px-3 py-2 text-xs font-medium tracking-normal transition-colors"
                        style={{
                            borderColor: data.budget_mode === "range" ? "var(--pg-fg)" : "var(--pg-border)",
                            background: data.budget_mode === "range" ? "var(--pg-fg)" : "transparent",
                            color: data.budget_mode === "range" ? "var(--pg-bg)" : "var(--pg-fg-muted)",
                        }}
                    >
                        Pick a range
                    </button>
                    <button
                        type="button"
                        onClick={() => onField("budget_mode", "custom")}
                        data-testid="budget-mode-custom"
                        className="flex-1 rounded-lg border px-3 py-2 text-xs font-medium tracking-normal transition-colors"
                        style={{
                            borderColor: data.budget_mode === "custom" ? "var(--pg-fg)" : "var(--pg-border)",
                            background: data.budget_mode === "custom" ? "var(--pg-fg)" : "transparent",
                            color: data.budget_mode === "custom" ? "var(--pg-bg)" : "var(--pg-fg-muted)",
                        }}
                    >
                        Type my own
                    </button>
                </div>

                {data.budget_mode === "range" ? (
                    <div className="mt-2">
                        <Select value={data.estimated_budget || undefined} onValueChange={(v) => onField("estimated_budget", v)}>
                            <SelectTrigger
                                id="budget-picker"
                                className="h-12 rounded-xl pg-input"
                                style={{ background: "var(--pg-bg)" }}
                                data-testid="budget-range-select"
                            >
                                <SelectValue placeholder="Pick a range" />
                            </SelectTrigger>
                            <SelectContent>
                                {BUDGET_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <FieldError error={errors?.estimated_budget} />
                    </div>
                ) : (
                    <div className="relative mt-2">
                        <span
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                            style={{ color: "var(--pg-fg-muted)" }}
                        >
                            $
                        </span>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={data.custom_budget}
                            onChange={(e) => onField("custom_budget", e.target.value)}
                            placeholder="e.g. 5,000 or 25,000"
                            data-testid="budget-custom-input"
                            className="pg-input pl-8"
                        />
                        <FieldError error={errors?.custom_budget} />
                    </div>
                )}
            </div>

            {/* Timeline */}
            <div className="flex flex-col gap-2">
                <FieldLabel htmlFor="budget-timeline">Expected Timeline</FieldLabel>
                <Select value={data.expected_timeline || undefined} onValueChange={(v) => onField("expected_timeline", v)}>
                    <SelectTrigger
                        id="budget-timeline"
                        className="h-12 rounded-xl pg-input"
                        style={{ background: "var(--pg-bg)" }}
                        data-testid="budget-timeline-select"
                    >
                        <SelectValue placeholder="When do you need this?" />
                    </SelectTrigger>
                    <SelectContent>
                        {TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                </Select>
                <FieldError error={errors?.expected_timeline} />
            </div>
        </div>

        <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="mt-6 text-[11px] leading-relaxed"
            style={{ color: "var(--pg-fg-muted)" }}
        >
            After you submit, we’ll review your Custom Project Inquiry and reply with a tailored
            Custom Engagement proposal within 24h on business days.
        </motion.p>
    </section>
);

export default StepBudget;
